(function () {
  if (customElements.get('product-media-slider')) return;

  class ProductMediaSlider extends HTMLElement {
    constructor() {
      super();
      this.viewport = this.querySelector('.pms__viewport');
      this.track = this.querySelector('.pms__track');
      this.prevButton = this.querySelector('[name="previous"]');
      this.nextButton = this.querySelector('[name="next"]');
      this.dotButtons = Array.from(this.querySelectorAll('.pms__dot'));
      this.slideTemplates = [];
      this.slides = [];
      this.originalCount = 0;
      this.currentIndex = 0;
      this.cloneCount = 0;
      this.currentSlidesPerView = 1;
      this.useFade = false;
      this.autoplayTimer = null;
      this.resizeTimer = null;
      this.pointerId = null;
      this.isPointerDown = false;
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragStartY = 0;
      this.dragDelta = 0;
      this.dragStartOffset = 0;
      this.suppressClick = false;

      this.handleResize = this.handleResize.bind(this);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.onButtonClick = this.onButtonClick.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onVideoToggle = this.onVideoToggle.bind(this);
      this.handleMediaLoaded = this.handleMediaLoaded.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onDragClick = this.onDragClick.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.readSettings();
      this.captureTemplates();
      this.bindStaticEvents();
      this.rebuild(0);
    }

    disconnectedCallback() {
      this.stopAutoplay();
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.removeEventListener('click', this.onVideoToggle);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.track?.removeEventListener('transitionend', this.handleTransitionEnd);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointermove', this.onPointerMove);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
      this.viewport?.removeEventListener('click', this.onDragClick, true);
      this.prevButton?.removeEventListener('click', this.onButtonClick);
      this.nextButton?.removeEventListener('click', this.onButtonClick);
      this.dotButtons.forEach((button) => button.removeEventListener('click', this.onDotClick));
      window.removeEventListener('resize', this.handleResize);
    }

    readSettings() {
      const parseBool = (value) => value === 'true';
      const parseNumber = (value, fallback = 0) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      this.settings = {
        direction: this.dataset.direction === 'vertical' ? 'vertical' : 'horizontal',
        slidesDesktop: Math.max(1, parseNumber(this.dataset.slidesDesktop, 1)),
        slidesMobile: Math.max(1, parseNumber(this.dataset.slidesMobile, 1)),
        desktopSpacing: Math.max(0, parseNumber(this.dataset.desktopSpacing, 0)),
        mobileSpacing: Math.max(0, parseNumber(this.dataset.mobileSpacing, 0)),
        infinite: parseBool(this.dataset.infinite),
        autoplay: parseBool(this.dataset.autoplay),
        autoplaySpeed: Math.max(1000, parseNumber(this.dataset.autoplaySpeed, 5000)),
        centerMode: parseBool(this.dataset.centerMode),
        fade: parseBool(this.dataset.fade),
      };
    }

    captureTemplates() {
      if (!this.track) return;
      this.slideTemplates = Array.from(this.track.children).map((slide) => slide.cloneNode(true));
      this.originalCount = this.slideTemplates.length;
    }

    bindStaticEvents() {
      this.prevButton?.addEventListener('click', this.onButtonClick);
      this.nextButton?.addEventListener('click', this.onButtonClick);
      this.dotButtons.forEach((button) => button.addEventListener('click', this.onDotClick));
      this.addEventListener('click', this.onVideoToggle);
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.track?.addEventListener('transitionend', this.handleTransitionEnd);
      this.viewport?.addEventListener('pointerdown', this.onPointerDown);
      this.viewport?.addEventListener('pointermove', this.onPointerMove);
      this.viewport?.addEventListener('pointerup', this.onPointerUp);
      this.viewport?.addEventListener('pointercancel', this.onPointerUp);
      this.viewport?.addEventListener('click', this.onDragClick, true);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('resize', this.handleResize);
    }

    bindMediaEvents() {
      this.querySelectorAll('img').forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', this.handleMediaLoaded, { once: true });
      });

      this.querySelectorAll('video').forEach((video) => {
        if (video.readyState >= 1) return;
        video.addEventListener('loadedmetadata', this.handleMediaLoaded, { once: true });
      });
    }

    handleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        this.rebuild(this.getLogicalIndex());
      }, 100);
    }

    handleTransitionEnd(event) {
      if (event.target !== this.track || this.useFade || !this.cloneCount) return;

      if (this.currentIndex < this.cloneCount) {
        this.currentIndex += this.originalCount;
        this.applyTransform(false);
      } else if (this.currentIndex >= this.cloneCount + this.originalCount) {
        this.currentIndex -= this.originalCount;
        this.applyTransform(false);
      }

      this.updateUi();
    }

    handleMouseEnter() {
      this.stopAutoplay();
    }

    handleMouseLeave() {
      this.startAutoplay();
    }

    handleFocusIn() {
      this.stopAutoplay();
    }

    handleFocusOut(event) {
      if (!this.contains(event.relatedTarget)) this.startAutoplay();
    }

    handleVisibilityChange() {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    }

    handleMediaLoaded() {
      this.layout(false);
    }

    getSlidesPerView() {
      return window.matchMedia('(min-width: 750px)').matches
        ? this.settings.slidesDesktop
        : this.settings.slidesMobile;
    }

    getGap() {
      return window.matchMedia('(min-width: 750px)').matches
        ? this.settings.desktopSpacing
        : this.settings.mobileSpacing;
    }

    canLoop() {
      return this.settings.infinite && this.originalCount > 1;
    }

    shouldUseFade() {
      return this.settings.fade && this.settings.slidesDesktop === 1 && this.settings.slidesMobile === 1 && this.originalCount > 1;
    }

    rebuild(logicalIndex = 0) {
      if (!this.track || !this.slideTemplates.length) return;

      this.stopAutoplay();

      this.useFade = this.shouldUseFade();
      this.currentSlidesPerView = this.getSlidesPerView();
      this.cloneCount = this.canLoop() && !this.useFade
        ? Math.min(this.originalCount, Math.max(1, Math.ceil(this.currentSlidesPerView)))
        : 0;

      const fragment = document.createDocumentFragment();
      const appendSlide = (template, logical, isClone) => {
        const node = template.cloneNode(true);
        node.dataset.logicalIndex = logical;
        if (isClone) node.dataset.clone = 'true';
        fragment.appendChild(node);
      };

      if (this.cloneCount) {
        for (let index = this.originalCount - this.cloneCount; index < this.originalCount; index += 1) {
          appendSlide(this.slideTemplates[index], index, true);
        }
      }

      this.slideTemplates.forEach((template, index) => appendSlide(template, index, false));

      if (this.cloneCount) {
        for (let index = 0; index < this.cloneCount; index += 1) {
          appendSlide(this.slideTemplates[index], index, true);
        }
      }

      this.track.innerHTML = '';
      this.track.appendChild(fragment);
      this.slides = Array.from(this.track.children);
      this.bindMediaEvents();

      const normalizedLogicalIndex = Math.max(0, Math.min(logicalIndex, Math.max(0, this.originalCount - 1)));
      this.currentIndex = this.useFade
        ? normalizedLogicalIndex
        : this.cloneCount
          ? normalizedLogicalIndex + this.cloneCount
          : normalizedLogicalIndex;

      this.layout(false);
      this.updateUi();
      this.startAutoplay();
    }

    layout(animate = true) {
      if (!this.slides.length || !this.viewport) return;

      const gap = this.getGap();
      const vertical = this.settings.direction === 'vertical';
      this.style.setProperty('--pms-gap', `${gap}px`);
      this.classList.toggle('is-vertical', vertical);
      this.classList.toggle('is-fade', this.useFade);

      if (this.useFade) {
        this.track.style.transition = 'none';
        this.track.style.transform = 'none';
        this.track.style.flexDirection = 'row';
        this.slides.forEach((slide, index) => {
          slide.style.width = '100%';
          slide.style.flex = '0 0 100%';
          slide.classList.toggle('is-active', index === this.currentIndex);
        });
        requestAnimationFrame(() => this.updateViewportHeight());
        this.syncVideos();
        return;
      }

      this.track.style.flexDirection = vertical ? 'column' : 'row';

      if (!vertical) {
        const viewportWidth = this.viewport.clientWidth;
        const slideWidth = Math.max(0, (viewportWidth - gap * (this.currentSlidesPerView - 1)) / this.currentSlidesPerView);
        this.slides.forEach((slide) => {
          slide.style.width = `${slideWidth}px`;
          slide.style.flex = `0 0 ${slideWidth}px`;
        });
        this.viewport.style.height = '';
      } else {
        this.slides.forEach((slide) => {
          slide.style.width = '100%';
          slide.style.flex = '0 0 auto';
        });
        requestAnimationFrame(() => this.updateVerticalViewportHeight(gap));
      }

      requestAnimationFrame(() => {
        this.applyTransform(animate);
        this.syncVideos();
      });
    }

    updateViewportHeight() {
      const activeSlide = this.slides[this.currentIndex];
      if (!activeSlide || !this.viewport) return;
      const height = activeSlide.getBoundingClientRect().height;
      if (height > 0) this.viewport.style.height = `${height}px`;
    }

    updateVerticalViewportHeight(gap) {
      if (!this.viewport) return;
      const visibleCount = Math.max(1, Math.ceil(this.currentSlidesPerView));
      let height = 0;

      for (let index = 0; index < visibleCount; index += 1) {
        const slide = this.slides[this.currentIndex + index] || this.slides[this.slides.length - 1];
        if (!slide) continue;
        height += slide.getBoundingClientRect().height;
        if (index < visibleCount - 1) height += gap;
      }

      if (height > 0) this.viewport.style.height = `${height}px`;
    }

    getTranslateOffset() {
      const vertical = this.settings.direction === 'vertical';
      const activeSlide = this.slides[this.currentIndex];
      if (!activeSlide || !this.viewport) return 0;

      let offset = vertical ? activeSlide.offsetTop : activeSlide.offsetLeft;
      const viewportSize = vertical ? this.viewport.clientHeight : this.viewport.clientWidth;
      const slideSize = vertical ? activeSlide.getBoundingClientRect().height : activeSlide.getBoundingClientRect().width;

      if (this.settings.centerMode) {
        offset -= Math.max(0, (viewportSize - slideSize) / 2);
      }

      const trackSize = vertical ? this.track.scrollHeight : this.track.scrollWidth;
      const maxOffset = Math.max(0, trackSize - viewportSize);

      return Math.max(0, Math.min(offset, maxOffset));
    }

    getCurrentOffset() {
      const transform = window.getComputedStyle(this.track).transform;
      if (!transform || transform === 'none') return 0;

      const MatrixConstructor = window.DOMMatrixReadOnly || window.DOMMatrix || window.WebKitCSSMatrix;
      if (!MatrixConstructor) return this.getTranslateOffset();

      const matrix = new MatrixConstructor(transform);
      return this.settings.direction === 'vertical' ? -matrix.m42 : -matrix.m41;
    }

    getSnapThreshold() {
      const activeSlide = this.slides[this.currentIndex];
      if (!activeSlide) return 40;

      const size = this.settings.direction === 'vertical'
        ? activeSlide.getBoundingClientRect().height
        : activeSlide.getBoundingClientRect().width;

      return Math.max(35, Math.min(size * 0.2, 120));
    }

    applyTransform(animate = true) {
      if (this.useFade) return;
      const offset = this.getTranslateOffset();
      this.track.style.transition = animate ? 'transform var(--pms-transition-duration) ease' : 'none';
      this.track.style.transform = this.settings.direction === 'vertical'
        ? `translate3d(0, ${-offset}px, 0)`
        : `translate3d(${-offset}px, 0, 0)`;
    }

    applyDragTransform(offset) {
      if (this.useFade) return;

      const vertical = this.settings.direction === 'vertical';
      const viewportSize = vertical ? this.viewport.clientHeight : this.viewport.clientWidth;
      const trackSize = vertical ? this.track.scrollHeight : this.track.scrollWidth;
      const maxOffset = Math.max(0, trackSize - viewportSize);
      const boundedOffset = Math.max(0, Math.min(offset, maxOffset));

      this.track.style.transition = 'none';
      this.track.style.transform = vertical
        ? `translate3d(0, ${-boundedOffset}px, 0)`
        : `translate3d(${-boundedOffset}px, 0, 0)`;
    }

    getLogicalIndex() {
      if (!this.originalCount) return 0;
      if (this.useFade) return this.currentIndex;
      const rawIndex = this.currentIndex - this.cloneCount;
      return ((rawIndex % this.originalCount) + this.originalCount) % this.originalCount;
    }

    getMaxIndex() {
      if (this.settings.centerMode || this.useFade) return Math.max(0, this.originalCount - 1);
      return Math.max(0, this.originalCount - Math.ceil(this.currentSlidesPerView));
    }

    updateUi() {
      const logicalIndex = this.getLogicalIndex();
      const maxIndex = this.getMaxIndex();

      this.slides.forEach((slide, index) => {
        const isCurrent = index === this.currentIndex;
        slide.classList.toggle('is-active', isCurrent);
        slide.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
      });

      this.dotButtons.forEach((button) => {
        const isActive = Number(button.dataset.index) === logicalIndex;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      if (!this.canLoop()) {
        this.prevButton?.toggleAttribute('disabled', logicalIndex <= 0);
        this.nextButton?.toggleAttribute('disabled', logicalIndex >= maxIndex);
      } else {
        this.prevButton?.removeAttribute('disabled');
        this.nextButton?.removeAttribute('disabled');
      }

      if (this.useFade) {
        requestAnimationFrame(() => this.updateViewportHeight());
      }

      this.syncVideos();
    }

    goTo(index, animate = true) {
      const boundedIndex = Math.max(0, Math.min(index, Math.max(0, this.originalCount - 1)));

      if (this.useFade) {
        this.currentIndex = boundedIndex;
        this.updateUi();
        this.resetAutoplay();
        return;
      }

      this.currentIndex = this.cloneCount ? boundedIndex + this.cloneCount : boundedIndex;
      this.applyTransform(animate);
      this.updateUi();
      this.resetAutoplay();
    }

    goToPrevious() {
      if (this.useFade) {
        const nextIndex = this.canLoop()
          ? (this.currentIndex - 1 + this.originalCount) % this.originalCount
          : Math.max(0, this.currentIndex - 1);
        this.currentIndex = nextIndex;
        this.updateUi();
        this.resetAutoplay();
        return;
      }

      if (this.cloneCount) {
        this.currentIndex -= 1;
        this.applyTransform(true);
        this.updateUi();
        this.resetAutoplay();
        return;
      }

      this.currentIndex = Math.max(0, this.currentIndex - 1);
      this.applyTransform(true);
      this.updateUi();
      this.resetAutoplay();
    }

    goToNext() {
      if (this.useFade) {
        const nextIndex = this.canLoop()
          ? (this.currentIndex + 1) % this.originalCount
          : Math.min(this.getMaxIndex(), this.currentIndex + 1);
        this.currentIndex = nextIndex;
        this.updateUi();
        this.resetAutoplay();
        return;
      }

      if (this.cloneCount) {
        this.currentIndex += 1;
        this.applyTransform(true);
        this.updateUi();
        this.resetAutoplay();
        return;
      }

      this.currentIndex = Math.min(this.getMaxIndex(), this.currentIndex + 1);
      this.applyTransform(true);
      this.updateUi();
      this.resetAutoplay();
    }

    onButtonClick(event) {
      event.preventDefault();
      if (event.currentTarget.name === 'previous') this.goToPrevious();
      else this.goToNext();
    }

    onDotClick(event) {
      event.preventDefault();
      this.goTo(Number(event.currentTarget.dataset.index));
    }

    onPointerDown(event) {
      if (event.button !== 0 || !this.track || !this.slides.length) return;
      if (event.target.closest('button, input, select, textarea')) return;

      this.pointerId = event.pointerId;
      this.isPointerDown = true;
      this.isDragging = false;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragDelta = 0;
      this.dragStartOffset = this.getCurrentOffset();
      this.stopAutoplay();
      this.viewport.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;
      const vertical = this.settings.direction === 'vertical';
      const primaryDelta = vertical ? deltaY : deltaX;
      const secondaryDelta = vertical ? deltaX : deltaY;

      if (!this.isDragging) {
        if (Math.abs(primaryDelta) < 8) return;
        if (Math.abs(primaryDelta) <= Math.abs(secondaryDelta)) return;
        this.isDragging = true;
      }

      event.preventDefault();
      this.dragDelta = primaryDelta;
      this.applyDragTransform(this.dragStartOffset - primaryDelta);
    }

    onPointerUp(event) {
      if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

      this.viewport.releasePointerCapture?.(event.pointerId);
      this.isPointerDown = false;
      this.pointerId = null;

      if (!this.isDragging) {
        this.startAutoplay();
        return;
      }

      const threshold = this.getSnapThreshold();
      if (this.dragDelta <= -threshold) {
        this.goToNext();
      } else if (this.dragDelta >= threshold) {
        this.goToPrevious();
      } else {
        this.applyTransform(true);
        this.updateUi();
        this.resetAutoplay();
      }

      this.isDragging = false;
      this.suppressClick = true;
      window.setTimeout(() => {
        this.suppressClick = false;
      }, 120);
    }

    onDragClick(event) {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    }

    onVideoToggle(event) {
      const toggle = event.target.closest('.pms__video-toggle');
      if (!toggle) return;

      event.preventDefault();
      event.stopPropagation();

      const wrap = toggle.closest('.pms__internal-video');
      const video = wrap?.querySelector('.pms__video');

      if (!wrap || !video) return;

      if (video.paused) {
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
        wrap.classList.add('is-playing');
      } else {
        video.pause();
        wrap.classList.remove('is-playing');
      }
    }

    syncVideos() {
      if (!this.slides.length) return;

      const activeSlideIndexes = new Set();
      if (this.useFade || this.settings.centerMode) {
        activeSlideIndexes.add(this.currentIndex);
      } else {
        const visibleSlides = Math.max(1, Math.ceil(this.currentSlidesPerView));
        for (let index = 0; index < visibleSlides; index += 1) {
          if (this.slides[this.currentIndex + index]) activeSlideIndexes.add(this.currentIndex + index);
        }
      }

      this.slides.forEach((slide, index) => {
        const wrap = slide.querySelector('.pms__internal-video');
        const video = wrap?.querySelector('.pms__video');
        if (!wrap || !video) return;

        const shouldAutoplay = slide.dataset.mutedAutoplay === 'true' && activeSlideIndexes.has(index);
        if (shouldAutoplay) {
          video.muted = true;
          const playPromise = video.play();
          if (playPromise?.catch) playPromise.catch(() => {});
          wrap.classList.add('is-playing');
        } else {
          video.pause();
          wrap.classList.remove('is-playing');
        }
      });
    }

    startAutoplay() {
      if (!this.settings.autoplay || this.originalCount < 2 || document.hidden) return;
      this.stopAutoplay();
      this.autoplayTimer = window.setInterval(() => this.goToNext(), this.settings.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    resetAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define('product-media-slider', ProductMediaSlider);
})();
